import { BrowserInitConfig, HttpPayload } from '@apmplus/web';

export type ApmOptions = Partial<NioApmPlusOptions> & Partial<ElasticApmOptions>;

export interface NioApmPlusOptions {
    /**
     * apm app id
     */
    aid: number;
    /**
     * apm app token
     */
    token: string;
    /**
     * 页面 id，无前端路由时可传
     */
    pid?: string;
    /**
     * 环境，不传走 SDK 默认填充 context 逻辑
     */
    env?: string;
    /**
     * 版本号，上传 source map 时，需与 source map 的版本号保持一致
     */
    release?: string;
    /**
     * 上报接口域名，非特殊场景不需要传
     */
    domain?: string;
    /**
     * 用户 id，不一定是数字 id，可以是 string，不传走 SDK 默认填充 context 逻辑
     */
    userId?: string;
    /**
     * 设备 id，不传走 SDK 默认填充 context 逻辑
     */
    deviceId?: string;
    /**
     * 插件，默认填充逻辑见 Options.Plugins 默认配置
     */
    plugins?: NioApmPlusPlugins;
    /**
     * 无须初始化传 false 或不传，初始化前是否在安卓 lg 容器中调用 window._dsbridge.init();
     * (安卓中会执行完init后等待100ms确保初始化完成)
     */
    needInitJSB?: boolean;
    /**
     * DEBUG 模式，日志不会上报，只在控制台中打印
     */
    isDebug?: boolean;
    /**
     * 自定义插件
     */
    integrations?: BrowserInitConfig['integrations'];
    /**
     * 自定义全局上下文
     */
    context?: Array<{ key: string; value: string }>;
    /**
     * 采样率配置
     */
    sample?: BrowserInitConfig['sample'];
}

export interface ElasticApmOptions {
    /**
     * 当前不支持海外环境，非 'cn' 时直接返回 null
     */
    region: 'cn' | 'eu' | 'other';
    /**
     * 用户 id，不一定是数字 id，可以是 string，不传走 SDK 默认填充 context 逻辑
     */
    userId?: string;
    /**
     * 设备 id，不传走 SDK 默认填充 context 逻辑
     */
    deviceId?: string;
    /**
     * Set required service name (allowed characters: a-z, A-Z, 0-9, -, _, and space)
     */
    serviceName: string;
    /**
    /**
     * 版本号，上传 source map 时，需与 source map 的版本号保持一致
     */
    release?: string;
    /**
     * test ｜ stg ｜ prod
     */
    serviceEnv: string;
    /**
     * is CompressionStream
     */
    isCompressionStream?: boolean;
    /**
     * 
     */
    vueErrorHandler: NioApmPlusVueErrorHandlerPluginConfig,
    /**
     * 北极星采样率配置
     */
    northPolarisSampleRateConfig: NorthPolarisConfig,
    /**
     * 自定义全局上下文
     */
    context?: Array<{ key: string; value: string }>;
    /**
     * AB灰度开关
     */
    abTest: boolean,
    /**
     * plugins
     */
    plugins?: any;
}

export interface NorthPolarisConfig {
    key: string;
}

export interface ElasticEventPayload {
    /** 自定义名称, type 为 event时必填 */
    name: string;
    /** 日志事件信息 */
    context?: string;
}

export interface ElasticApm {
    startTransaction?: (name: string | undefined, type?: string | undefined, options?: any) => Transaction | undefined;
    startSpan?: (name: string | undefined, type?: string | undefined, options?: any) => Span | undefined;
    captureError?: (error: any, opts?: any) => Promise<void>;
    sendEvent?: (event: ElasticEventPayload) => void;
    sendPageView?: (pageId: string, type: string, labels: any) => void;
    warn(content: string, extra?: { [key: string]: string | number }): void;
    info(content: string, extra?: { [key: string]: string | number }): void;
    error(content: string, extra?: { [key: string]: string | number }): void;
    debug(content: string, extra?: { [key: string]: string | number }): void;
    addLabels(tags: Record<string, any>): void;
    setCustomContext(customContext: Record<string, any>): void;
    setUserContext(userContext: { id?: string | number; username?: string; email?: string }): void;
    destroy(): void;
}

export interface Transaction {
    startSpan?: (name: string | undefined, type?: string | undefined, options?: any) => Span | undefined;
    end?: (endTime?: number | undefined) => void;
}

export interface Span {
    end?: (endTime?: number | undefined) => void;
}

export interface NioApmPlusPlugins {
    jsError?: Partial<JsErrorMonitorConfig> | boolean;
    performance?: Partial<PerformanceMonitorUserConfig> | boolean;
    blankScreen?: Partial<BlankScreenMonitorConfig> | boolean;
    resource?: Partial<ResourceMonitorConfig> | boolean;
    resourceError?: Partial<ResourceErrorMonitorConfig> | boolean;
    pageview?: Partial<PageviewMonitorConfig> | boolean;
    ajax?: Partial<AjaxMonitorConfig> | boolean;
    fetch?: Partial<FetchMonitorConfig> | boolean;
    vueErrorHandler?: NioApmPlusVueErrorHandlerPluginConfig;
    autoReportClickEvent?: NioApmPlusAutoReportClickEventConfig;
    breadcrumb?: Partial<BreadcrumbConfig> | boolean;
    qiankun?: NioApmPlusQiankunPluginConfig;
    /**
     * SPA 应用路由切换性能计算插件
     */
    spaLoad?: boolean;
}

export interface NioApmPlusQiankunPluginConfig {
    /**
     * @deprecated
     * 此方法设计时命名易产生误导，请替换为 getCurrentAppReleaseVersion 方法
     * 上报日志前执行，返回当前页面所属 qiankun 主/子应用的 release 版本号，和子应用的 Sourcemap Release 版本号对应，用于解析 JS Error 的源代码
     */
    getCurrentMicroAppReleaseVersion?: (options: GetCurrentMicroAppReleaseVersionOptions) => string;
    /**
     * 上报 JS Error 日志前执行，返回当前页面所属 qiankun 主/子应用的 release 版本号，需和应用代码的 Sourcemap Release 版本号对应，用于解析 JS Error 的源代码
     * 返回 null 或 undefined 则不修改该条日志的 release
     */
    getCurrentAppReleaseVersion?: (options: GetCurrentAppReleaseVersionOptions) => string | null | undefined;
}

/**
 * @deprecated
 */
export interface GetCurrentMicroAppReleaseVersionOptions {
    url?: string;
}

export interface GetCurrentAppReleaseVersionOptions {
    url?: string;
    /** 错误名称 */
    name?: string;
    /** 错误信息 */
    message: string;
    /** 堆栈 */
    stack?: string;
}

export type Falsy = false | null | undefined;

export interface Processor<E> {
    (e: E): E | Falsy;
}

export interface Breadcrumb {
    /** dom | http */
    type: string;
    /** xpath, keyvalue | url */
    message: string;
    /** ui.click, ui.keypress | post,get */
    category: string;
    /** status: 400 for http */
    data?: {
        [key: string]: string;
    };
    timestamp: number;
}

export type PreBreadcrumb = Omit<Breadcrumb, 'timestamp'> & Partial<Pick<Breadcrumb, 'timestamp'>>;
export type OnMaxBreadcrumbs = (breadcrumbs: Breadcrumb[], maxBreadcrumbs: number) => Breadcrumb[];

export interface BreadcrumbConfig {
    maxBreadcrumbs?: number;
    onAddBreadcrumb?: Processor<PreBreadcrumb>;
    onMaxBreadcrumbs?: OnMaxBreadcrumbs;
    dom?: boolean;
}

export interface NioApmPlusAutoReportClickEventConfig {
    enable: boolean;
    depth?: number;
    defaultType?: 'event' | 'log';
    defaultLogLevel?: 'debug' | 'info' | 'warn' | 'error';
}

export interface NioApmPlusVueErrorHandlerPluginConfig {
    app: Vue;
}

// This is not great, but kinda necessary to make it woth with Vue@2 and Vue@3 at the same time.
export interface Vue {
    config: {
        errorHandler?: any;
        silent?: boolean;
    };
    // mixin: (mixins: Partial<Record<Hook, any>>) => void;
}

// export type Hook =
//     | 'activated'
//     | 'beforeCreate'
//     | 'beforeDestroy'
//     | 'beforeMount'
//     | 'beforeUpdate'
//     | 'created'
//     | 'deactivated'
//     | 'destroyed'
//     | 'mounted'
//     | 'updated';

export type ViewModel = {
    _isVue?: boolean;
    __isVue?: boolean;
    $root: ViewModel;
    $parent?: ViewModel;
    $props: { [key: string]: any };
    $options?: {
        name?: string;
        propsData?: { [key: string]: any };
        _componentTag?: string;
        __file?: string;
    };
};

export interface FetchMonitorConfig extends AjaxMonitorConfig {}

export type HttpReport = {
    ev_type: 'http';
    payload: HttpPayload;
};
export type Cb = (ev: HttpReport) => void;

export interface AjaxMonitorConfig {
    autoWrap?: boolean;
    hookCbAtReq: (t: Cb | undefined) => Cb | undefined;
    ignoreUrls: (string | RegExp)[];
    collectBodyOnError: boolean;
    extraExtractor?: (
        body: string | any,
        related: HttpPayload
    ) =>
        | {
              [key: string]: string;
          }
        | false;
    trace?: TraceConfig | boolean;
    setTraceHeader?: (url: string, cb: (key: string, value: string) => void) => void;
}

export interface TraceConfig {
    sampleRate?: number;
    origins: (string | RegExp)[];
}

export interface ResourceMonitorConfig {
    ignoreUrls: (string | RegExp)[];
    slowSessionThreshold: number;
    ignoreTypes?: string[];
}

export type PageviewMonitorConfig = Omit<PageviewProps, 'onPidUpdate' | 'initPid'>;

export interface PageviewProps {
    sendInit: boolean;
    routeMode: 'history' | 'hash' | 'manual';
    initPid?: string;
    extractPid?: PidExtractor;
    onPidUpdate?: (pid: string) => void;
}

export interface PidExtractor {
    (url: string): string;
}

export interface ResourceErrorMonitorConfig {
    ignoreUrls: (string | RegExp)[];
    includeUrls: (string | RegExp)[];
    dedupe: boolean;
}

export interface BlankScreenMonitorConfig {
    autoDetect?: boolean;
    threshold: number;
    screenshot: boolean;
    rootSelector?: string;
    ssUrl: string;
    mask: boolean;
    partialShot: boolean;
    /**
     * 0 ~ 1,default to 0.1
     */
    quality: number;
}

export enum MetricEnum {
    fp = 'fp',
    fcp = 'fcp',
    fid = 'fid',
    mpfid = 'mpfid',
    lcp = 'lcp',
    cls = 'cls',
    longtask = 'longtask',
    timing = 'timing',
}

export type PerformanceMonitorUserConfig = {
    [key in MetricEnum]: boolean;
};

export interface JsErrorMonitorConfig {
    ignoreErrors: (string | RegExp)[];
    onerror: boolean;
    onunhandledrejection: boolean;
    captureGlobalAsync?: boolean;
    dedupe?: boolean;
}

export interface ReactInfo {
    version: string;
    componentStack: string;
}

export type CustomEventData = {
    /** 自定义事件名称 */
    name: string;
    /**  @deprecated (火山APM) metrics 上报的是可以被度量的值，也就是数值 */
    metrics?: {
        [key: string]: number;
    };
    /** @deprecated (火山APM)categories 上报的是分类，维度，用来做筛选，分组 */
    categories?: {
        [key: string]: string;
    };
    /** 日志事件信息(新APM) */
    context?: string;
};
